/**
 * Orchestration only — the actual equivalent-rate math lives PER UNIVERSITY
 * in its own data module (kuwaitUniversityData, paaetData, foreignMissionsData,
 * internalMissionsData, abdullahAlSalemData). This file just walks each
 * university's colleges and dispatches to the right module's calculator
 * via the registry, so one university's math can never leak into another.
 *
 * `Scores` is re-exported from `sharedTypes` for backwards compatibility
 * with existing imports throughout the feature.
 */

import type { College, Department, Gender, Track } from "../data/sharedTypes";
import {
    type UniversityId,
    calculateEquivalentRate as registryCalculateEquivalentRate,
    getColleges,
    getCollegeMinRate as registryGetCollegeMinRate,
    getDepartmentThreshold,
    meetsMinEnglish as registryMeetsMinEnglish,
    meetsMinHighSchool as registryMeetsMinHighSchool,
} from "../data/universityRegistry";

export type { Scores } from "../data/sharedTypes";
import type { Scores } from "../data/sharedTypes";

/** Re-export thin wrappers so existing call sites keep working without
 *  having to import from the registry directly. Each call still dispatches
 *  to the per-university implementation. */
export function calculateEquivalentRate(
    universityId: UniversityId,
    weights: import("../data/sharedTypes").CollegeWeights,
    scores: Scores,
): number {
    return registryCalculateEquivalentRate(universityId, weights, scores);
}

export function meetsMinHighSchool(
    universityId: UniversityId,
    weights: import("../data/sharedTypes").CollegeWeights,
    scores: Scores,
): boolean {
    return registryMeetsMinHighSchool(universityId, weights, scores);
}

export function meetsMinEnglish(
    universityId: UniversityId,
    weights: import("../data/sharedTypes").CollegeWeights,
    scores: Scores,
): boolean {
    return registryMeetsMinEnglish(universityId, weights, scores);
}

export interface QualifiedCollege {
    college: College;
    studentRate: number;
    minRate: number;
}

export function getQualifiedColleges(universityId: UniversityId, scores: Scores, gender?: Gender): QualifiedCollege[] {
    const colleges = getColleges(universityId, gender);
    const qualified: QualifiedCollege[] = [];

    for (const college of colleges) {
        if (!registryMeetsMinHighSchool(universityId, college.weights, scores)) continue;
        if (!registryMeetsMinEnglish(universityId, college.weights, scores)) continue;

        const studentRate = registryCalculateEquivalentRate(universityId, college.weights, scores);
        const minRate = registryGetCollegeMinRate(universityId, college.key, gender);

        if (studentRate >= minRate) {
            qualified.push({ college, studentRate, minRate });
        }
    }

    qualified.sort((a, b) => b.studentRate - a.studentRate);

    return qualified;
}

export interface QualifiedDepartment {
    department: Department;
    studentRate: number;
}

export interface QualifiedCollegeWithDepts {
    college: College;
    studentRate: number;
    departments: QualifiedDepartment[];
}

export function getQualifiedCollegesWithDepartments(
    universityId: UniversityId,
    scores: Scores,
    gender?: Gender,
    track?: Track | null,
): QualifiedCollegeWithDepts[] {
    const colleges = getColleges(universityId, gender);
    const result: QualifiedCollegeWithDepts[] = [];

    for (const college of colleges) {
        if (!registryMeetsMinHighSchool(universityId, college.weights, scores)) continue;
        if (!registryMeetsMinEnglish(universityId, college.weights, scores)) continue;

        const studentRate = registryCalculateEquivalentRate(universityId, college.weights, scores);

        const candidateDepts = track
            ? college.departments.filter((dept) => !dept.track || dept.track === track)
            : college.departments;

        const deptsWithRate = candidateDepts.map((dept) => ({
            dept,
            rate: dept.weightsOverride
                ? registryCalculateEquivalentRate(universityId, dept.weightsOverride, scores)
                : studentRate,
        }));

        // Per-section threshold: each module decides what value its students
        // are qualifying against (FM uses `minAcceptedLastYear ?? equivalentRate`;
        // every other section uses `equivalentRate`). All isolation lives in
        // the per-section data module.
        const qualifiedDepts = deptsWithRate
            .filter(({ dept, rate }) => {
                const threshold = getDepartmentThreshold(universityId, dept);
                return threshold > 0 && rate >= threshold;
            })
            .filter(({ dept }) => {
                if (!dept.extraScoreField || dept.minExtraScore == null) return true;
                const score = scores[dept.extraScoreField];
                return typeof score === "number" && score >= dept.minExtraScore;
            })
            .sort((a, b) =>
                getDepartmentThreshold(universityId, a.dept)
                - getDepartmentThreshold(universityId, b.dept),
            );

        if (qualifiedDepts.length > 0) {
            result.push({
                college,
                studentRate,
                departments: qualifiedDepts.map(({ dept, rate }) => ({
                    department: dept,
                    studentRate: rate,
                })),
            });
        }
    }

    result.sort((a, b) => b.departments.length - a.departments.length);

    return result;
}
